import React from 'react';
import { Form, Header, Table, Checkbox } from 'semantic-ui-react';
import {
  CampaignScheduleBlock,
  CampaignScheduleDocument,
  CampaignScheduleDay,
} from '../../../types/campaigns.types';
import {
  CAMPAIGN_SCHEDULE_DAY_LABELS,
  createDefaultScheduleBlock,
} from '../../../constants/campaignScoringDefaults';
import GlassmorphismTheme from '../../../styles/glassmorphism';

interface CampaignScheduleEditorProps {
  schedule: CampaignScheduleDocument;
  onChange: (schedule: CampaignScheduleDocument) => void;
}

const ensureBlock = (block?: CampaignScheduleBlock | null): CampaignScheduleBlock =>
  block?.days?.length === 7 ? block : createDefaultScheduleBlock();

const updateDay = (
  block: CampaignScheduleBlock,
  dayOfWeek: number,
  patch: Partial<CampaignScheduleDay>
): CampaignScheduleBlock => ({
  days: block.days.map((d) => (d.dayOfWeek === dayOfWeek ? { ...d, ...patch } : d)),
});

const renderDayTable = (
  block: CampaignScheduleBlock,
  onBlockChange: (block: CampaignScheduleBlock) => void
) => (
  <Table celled compact size="small">
    <Table.Header>
      <Table.Row>
        <Table.HeaderCell>Dia</Table.HeaderCell>
        <Table.HeaderCell>Ativo</Table.HeaderCell>
        <Table.HeaderCell>Início</Table.HeaderCell>
        <Table.HeaderCell>Fim</Table.HeaderCell>
      </Table.Row>
    </Table.Header>
    <Table.Body>
      {block.days
        .slice()
        .sort((a, b) => a.dayOfWeek - b.dayOfWeek)
        .map((day) => (
          <Table.Row key={day.dayOfWeek}>
            <Table.Cell>{CAMPAIGN_SCHEDULE_DAY_LABELS[day.dayOfWeek]}</Table.Cell>
            <Table.Cell textAlign="center">
              <Checkbox
                checked={day.enabled}
                onChange={(_, { checked }) =>
                  onBlockChange(updateDay(block, day.dayOfWeek, { enabled: !!checked }))
                }
              />
            </Table.Cell>
            <Table.Cell>
              <Form.Input
                type="time"
                disabled={!day.enabled}
                value={day.horaInicio?.slice(0, 5) ?? '00:00'}
                onChange={(e) =>
                  onBlockChange(
                    updateDay(block, day.dayOfWeek, { horaInicio: e.target.value || '00:00' })
                  )
                }
                style={GlassmorphismTheme.components.inputGlass}
              />
            </Table.Cell>
            <Table.Cell>
              <Form.Input
                type="time"
                disabled={!day.enabled}
                value={day.horaFim?.slice(0, 5) ?? '23:59'}
                onChange={(e) =>
                  onBlockChange(
                    updateDay(block, day.dayOfWeek, { horaFim: e.target.value || '23:59' })
                  )
                }
                style={GlassmorphismTheme.components.inputGlass}
              />
            </Table.Cell>
          </Table.Row>
        ))}
    </Table.Body>
  </Table>
);

const CampaignScheduleEditor: React.FC<CampaignScheduleEditorProps> = ({ schedule, onChange }) => {
  const metaBlock = ensureBlock(schedule.meta);
  const pontosBlock = ensureBlock(schedule.pontos);

  const patch = (partial: Partial<CampaignScheduleDocument>) => onChange({ ...schedule, ...partial });

  return (
    <>
      <Form.Field>
        <Form.Checkbox
          label="Usar grade de horários separada para pontuação"
          checked={schedule.usarGradeSeparadaPontos}
          onChange={(_, { checked }) =>
            patch({
              usarGradeSeparadaPontos: !!checked,
              pontos: checked ? pontosBlock : null,
            })
          }
        />
      </Form.Field>

      <Header as="h5" style={{ color: '#2c3e50', marginTop: '1rem' }}>
        Grade — Metas
      </Header>
      {renderDayTable(metaBlock, (meta) => patch({ meta }))}

      {schedule.usarGradeSeparadaPontos && (
        <>
          <Header as="h5" style={{ color: '#2c3e50', marginTop: '1rem' }}>
            Grade — Pontuação
          </Header>
          {renderDayTable(pontosBlock, (pontos) => patch({ pontos }))}
        </>
      )}
    </>
  );
};

export default CampaignScheduleEditor;
